// ============================================================================
// Complete Markdown Formatter Component with TypeScript
// Follows CommonMark Specification with GitHub Flavored Markdown Extensions
// ============================================================================

import React, { memo, useMemo, ReactNode, ReactElement, cloneElement, useState } from 'react';
import { Button } from '../components/atoms/button';
import Icon from '../components/molecules/Icon';
import { Copy, Check } from 'lucide-react';
import { marked } from 'marked';

// ============================================================================
// Type Definitions
// ============================================================================

export interface MarkdownFormatterOptions {
  /** Enable GitHub Flavored Markdown extensions */
  gfm?: boolean;
  /** Enable line breaks on single newlines */
  breaks?: boolean;
  /** Enable smart typography (quotes, dashes) */
  smartypants?: boolean;
  /** Enable task lists */
  taskLists?: boolean;
  /** Enable strikethrough */
  strikethrough?: boolean;
  /** Enable tables */
  tables?: boolean;
  /** Enable footnotes */
  footnotes?: boolean;
  /** Enable autolinks */
  autolink?: boolean;
  /** Enable syntax highlighting for code blocks */
  highlight?: boolean;
  /** Custom class prefix for all elements */
  classPrefix?: string;
  /** Sanitize HTML input */
  sanitize?: boolean;
  /** Enable heading anchors */
  headingAnchors?: boolean;
  /** Enable table of contents generation */
  toc?: boolean;
  /** Enable RTL support for Arabic text */
  isRtl?: boolean;
}

export interface MarkdownElement {
  type: string;
  content?: string;
  children?: MarkdownElement[];
  props?: Record<string, any>;
}

interface ListContext {
  type: 'ul' | 'ol' | 'task';
  depth: number;
  start?: number;
  tight?: boolean;
  marker?: string;
}

// interface TableCell {
//   content: string;
//   align?: 'left' | 'center' | 'right';
// }

// interface CodeBlockInfo {
//   language?: string;
//   meta?: string;
// }

interface LinkReference {
  label: string;
  url: string;
  title?: string;
}

// ============================================================================
// Constants and Precompiled Patterns
// ============================================================================

// Frozen regex patterns for performance - reset lastIndex before use
const PATTERNS = Object.freeze({
  // Block elements
  HEADING: /^(#{1,6})\s*(.+)$/,
  HEADING_ALT: /^(.+)\n([=-])+$/,
  HEADING_WITH_ID: /^(#{1,6})\s*(.+?)\s*\{#([a-z0-9-]+)\}\s*$/,
  HORIZONTAL_RULE: /^(?:[-*_]){3,}\s*$/,
  BLOCKQUOTE: /^>\s?(.*)$/,
  CODE_BLOCK: /^```([a-z0-9_+-]*)?(?:\s+(.+))?\s*$/,
  INDENTED_CODE: /^(?: {4}|\t)(.*)$/,
  LIST_ITEM: /^(\s*)([-*+]|\d+[.):])\s+(.*)$/,
  TASK_LIST: /^(\s*)([-*+])\s+\[([xX\s])\]\s+(.*)$/,
  TABLE_ROW: /^\|(.+)\|$/,
  TABLE_SEPARATOR: /^\|[\s:|-]+\|$/,
  REFERENCE_DEF: /^\[([^\]]+)\]:\s*([^\s]+)(?:\s+"([^"]+)")?\s*$/,
  FOOTNOTE_DEF: /^\[\^([^\]]+)\]:\s*(.+)$/,
  HTML_BLOCK: /^<([a-z][a-z0-9-]*)(?: [^>]*)?>.*$/i,
  HTML_SELF_CLOSING: /^<([a-z][a-z0-9-]*)(?: [^>]*)?\/>\s*$/i,
  
  // Inline elements
  BOLD: /\*\*([^*]+)\*\*/g,
  ITALIC: /(?<!\*)\*([^*]+)\*(?!\*)/g,
  BOLD_ITALIC: /\*\*\*([^*]+)\*\*\*/g,
  STRIKETHROUGH: /~~([^~]+)~~/g,
  INLINE_CODE: /`([^`]+)`/g,
  LINK: /\[([^\]]+)\]\(([^)]+?)(?:\s+"([^"]+)")?\)/g,
  IMAGE: /!\[([^\]]*)\]\(([^)]+?)(?:\s+"([^"]+)")?\)/g,
  REFERENCE_LINK: /\[([^\]]+)\]\[([^\]]*)\]/g,
  AUTOLINK: /<((?:https?|ftp|mailto):[^>]+)>/g,
  EMAIL_AUTOLINK: /<([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})>/g,
  FOOTNOTE_REF: /\[\^([^\]]+)\]/g,
  LINE_BREAK: /  $/,
  ESCAPE: /\\([\\`*_{}[\]()#+\-.!|])/g,
  HTML_TAG: /<[^>]+>/g,
  EMOJI: /:([\w+-]+):/g,
  
  // Special patterns
  FOLLOW_UP: /<\s*FOLLOW_UP\s*\/>\s*/gi,
  MENTION: /@([a-zA-Z0-9_-]+)/g,
  HASHTAG: /#([a-zA-Z0-9_]+)/g,
  MATH_INLINE: /\$([^$]+)\$/g,
  MATH_BLOCK: /\$\$([^$]+)\$\$/g,
});

// CommonMark block-level HTML tags (not currently used)
// const BLOCK_LEVEL_TAGS = new Set([
//   'address', 'article', 'aside', 'blockquote', 'canvas', 'dd', 'div', 'dl', 'dt',
//   'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4',
//   'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'noscript', 'ol', 'p', 'pre',
//   'section', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul', 'video'
// ]);

// Emoji mappings (subset)
const EMOJI_MAP: Record<string, string> = {
  'smile': '😊',
  'thumbsup': '👍',
  'heart': '❤️',
  'star': '⭐',
  'fire': '🔥',
  'rocket': '🚀',
  'warning': '⚠️',
  'check': '✅',
  'x': '❌',
  'bulb': '💡',
  // Add more as needed
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Clean redundant bullet symbols from list content
 */
const cleanListContent = (content: string): string => {
  // Remove leading bullet symbols (•, ◦, ▪, ▫, etc.) that are redundant in HTML lists
  // But preserve content that starts with formatted numbering like **1.** or *a)*
  
  // Don't remove bullets if the content after cleaning would have its own numbering
  const withoutBullets = content.replace(/^[\s]*[•◦▪▫‣⁃●]\s*/, '').trim();
  if (hasOwnNumbering(withoutBullets)) {
    return withoutBullets;
  }
  
  // Otherwise, remove bullets as usual
  return content.replace(/^[\s]*[•◦▪▫‣⁃●]\s*/, '').trim();
};

/**
 * Check if content already starts with numbering (1., I., a), etc.)
 */
const hasOwnNumbering = (content: string): boolean => {
  const trimmed = content.trim();
  // Check for patterns like: 1. 2. 10. I. II. III. IV. V. a) b) A) B) etc.
  // Also handle bold/italic formatting around numbers: **1.**, *2.*, etc.
  const numberingPatterns = [
    /^\*?\*?\d+[.)]\s/, // Arabic numerals with optional bold: 1. 2. **10.** *3.* etc.
    /^\*?\*?[IVXLCDMivxlcdm]+[.)]\s/, // Roman numerals with optional formatting: I. **II.** *iii)* etc.
    /^\*?\*?[a-zA-Z][.)]\s/, // Letters with optional formatting: a. **b.** *A)* etc.
    /^\*?\*?\([a-zA-Z0-9]+\)\s/, // Parenthetical with optional formatting: (a) **(1)** *(i)* etc.
    /^\*?\*?[a-zA-Z0-9]+\)\s/, // Simple letter/number with closing paren: a) **1)** *i)* etc.
    /^\*{1,3}\d+[.)]/, // Bold/italic numbers: **2.** ***3.*** *4.* etc.
    /^\*{1,3}[IVXLCDMivxlcdm]+[.)]/, // Bold/italic Roman numerals: **I.** ***II.*** *iii)* etc.
    /^\*{1,3}[a-zA-Z][.)]/ // Bold/italic letters: **a.** ***b.*** *A)* etc.
  ];
  
  return numberingPatterns.some(pattern => pattern.test(trimmed));
};
const resetPattern = (pattern: RegExp): RegExp => {
  pattern.lastIndex = 0;
  return pattern;
};

/**
 * Escape HTML entities to prevent XSS
 */
const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
};

/**
 * Generate slug from heading text for anchors
 */
const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

/**
 * Parse table alignment from separator row
 */
const parseTableAlignment = (separator: string): ('left' | 'center' | 'right' | null)[] => {
  return separator
    .split('|')
    .map(cell => cell.trim())
    .filter(cell => cell)
    .map(cell => {
      const hasLeft = cell.startsWith(':');
      const hasRight = cell.endsWith(':');
      if (hasLeft && hasRight) return 'center';
      if (hasRight) return 'right';
      if (hasLeft) return 'left';
      return null;
    });
};

/**
 * Parse table cell content with proper line break handling
 */
const parseTableCell = (cellContent: string, state: ParserState, keyBase: number): ReactNode => {
  if (!cellContent) return '';
  
  const options = state.getOptions();
  
  // Handle literal <br> and <br/> tags in cell content
  if (cellContent.includes('<br>') || cellContent.includes('<br/>') || cellContent.includes('<br />')) {
    const parts = cellContent.split(/(<br\s*\/?>)/i);
    const elements: ReactNode[] = [];
    
    parts.forEach((part, index) => {
      if (/^<br\s*\/?>$/i.test(part)) {
        elements.push(<br key={`br-${keyBase}-${index}`} />);
      } else if (part.trim()) {
        const parsed = parseInline(part.trim(), state, keyBase + (index * 10));
        if (parsed) {
          elements.push(parsed);
        }
      }
    });
    
    return elements.length === 0 ? '' : (elements.length === 1 ? elements[0] : elements);
  }
  
  // Handle line breaks in cell content
  if (options.breaks && cellContent.includes('\n')) {
    const lines = cellContent.split('\n');
    const elements: ReactNode[] = [];
    
    lines.forEach((line, index) => {
      if (index > 0) {
        elements.push(<br key={`br-${keyBase}-${index}`} />);
      }
      const parsed = parseInline(line.trim(), state, keyBase + (index * 10));
      if (parsed) {
        elements.push(parsed);
      }
    });
    
    return elements.length === 0 ? '' : (elements.length === 1 ? elements[0] : elements);
  }
  
  // For single line content, parse normally
  return parseInline(cellContent, state, keyBase);
};

/**
 * Clean message content
 */
export const cleanMessage = (message: string): string => {
  return message
    .replace(/^[,\s\n\r]+/, '')
    .replace(/[,\s\n\r]+$/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
};

/**
 * Clean user messages with attachments
 */
export const cleanUserMessageWithAttachments = (message: string): string => {
  let cleanedMessage = message.replace(/^User Message:\s*/i, '');
  const attachmentStart = cleanedMessage.indexOf('--- Attached Documents ---');
  if (attachmentStart !== -1) {
    cleanedMessage = cleanedMessage.substring(0, attachmentStart);
  }
  return cleanedMessage.trim();
};

// ============================================================================
// Parser State Management
// ============================================================================

class ParserState {
  private lines: string[];
  private position: number = 0;
  private references: Map<string, LinkReference> = new Map();
  private footnotes: Map<string, string> = new Map();
  private usedFootnotes: Set<string> = new Set();
  private options: MarkdownFormatterOptions;
  private listStack: ListContext[] = [];
  private currentListCounter: Map<number, number> = new Map();
  
  constructor(text: string, options: MarkdownFormatterOptions = {}) {
    this.lines = text.split('\n');
    this.options = {
      gfm: true,
      breaks: false,
      smartypants: true,
      taskLists: true,
      strikethrough: true,
      tables: true,
      footnotes: true,
      autolink: true,
      highlight: true,
      classPrefix: '',
      sanitize: true,
      headingAnchors: true,
      toc: false,
      ...options
    };
    
    // Pre-process references and footnotes
    this.preprocessReferences();
  }
  
  private preprocessReferences(): void {
    const tempLines: string[] = [];
    
    for (const line of this.lines) {
      // Check for reference definitions
      const refMatch = line.match(PATTERNS.REFERENCE_DEF);
      if (refMatch) {
        const [, label, url, title] = refMatch;
        this.references.set(label.toLowerCase(), { label, url, title });
        continue;
      }
      
      // Check for footnote definitions
      if (this.options.footnotes) {
        const footnoteMatch = line.match(PATTERNS.FOOTNOTE_DEF);
        if (footnoteMatch) {
          const [, label, content] = footnoteMatch;
          this.footnotes.set(label, content);
          continue;
        }
      }
      
      tempLines.push(line);
    }
    
    this.lines = tempLines;
  }
  
  getCurrentLine(): string | null {
    return this.position < this.lines.length ? this.lines[this.position] : null;
  }
  
  peekLine(offset: number = 1): string | null {
    const pos = this.position + offset;
    return pos < this.lines.length ? this.lines[pos] : null;
  }
  
  advance(): void {
    this.position++;
  }
  
  isComplete(): boolean {
    return this.position >= this.lines.length;
  }
  
  getReference(label: string): LinkReference | undefined {
    return this.references.get(label.toLowerCase());
  }
  
  getFootnote(label: string): string | undefined {
    this.usedFootnotes.add(label);
    return this.footnotes.get(label);
  }
  
  getOptions(): MarkdownFormatterOptions {
    return this.options;
  }
  
  enterList(context: ListContext): void {
    this.listStack.push(context);
  }
  
  exitList(): void {
    this.listStack.pop();
  }
  
  getCurrentList(): ListContext | undefined {
    return this.listStack[this.listStack.length - 1];
  }
  
  getListDepth(): number {
    return this.listStack.length;
  }
  
  updateListCounter(depth: number, value: number): void {
    this.currentListCounter.set(depth, value);
  }
  
  getListCounter(depth: number): number {
    return this.currentListCounter.get(depth) || 1;
  }
  
  getPosition(): number {
    return this.position;
  }
  
  getAllLines(): string[] {
    return this.lines;
  }
  
  getUsedFootnotes(): Set<string> {
    return this.usedFootnotes;
  }
}

// ============================================================================
// Inline Parsing Functions
// ============================================================================

/**
 * Parse inline elements with full CommonMark support
 */
const parseInline = (text: string, state: ParserState, keyBase: number = 0): ReactNode => {
  if (!text) return null;
  
  const options = state.getOptions();
  const elements: ReactNode[] = [];
  let keyCounter = keyBase;
  
  // Handle escapes first
  text = text.replace(resetPattern(PATTERNS.ESCAPE), '$1');
  
  // Smart typography
  if (options.smartypants) {
    text = text
      .replace(/--/g, '—')
      .replace(/\.\.\./g, '…')
      .replace(/``/g, '"')
      .replace(/''/g, '"')
      .replace(/^'|(?<=\s)'/g, '\u2018')  // Left single quotation mark
      .replace(/'$/g, '\u2019');          // Right single quotation mark
  }
  
  // Process different inline patterns based on priority
  const processedSegments: Array<{ start: number; end: number; element: ReactNode }> = [];
  
  // 1. Process code spans (highest priority - no nested formatting)
  let match;
  resetPattern(PATTERNS.INLINE_CODE);
  while ((match = PATTERNS.INLINE_CODE.exec(text)) !== null) {
    processedSegments.push({
      start: match.index,
      end: match.index + match[0].length,
      element: (
        <code 
          key={`code-${keyCounter++}`}
          className={`${options.classPrefix}inline-code bg-gray-100 text-gray-900 px-1 py-0.5 rounded text-sm font-mono`}
        >
          {match[1]}
        </code>
      )
    });
  }
  
  // 2. Process links and images
  if (!isWithinProcessed(0, text.length, processedSegments)) {
    resetPattern(PATTERNS.IMAGE);
    while ((match = PATTERNS.IMAGE.exec(text)) !== null) {
      if (!isWithinProcessed(match.index, match.index + match[0].length, processedSegments)) {
        processedSegments.push({
          start: match.index,
          end: match.index + match[0].length,
          element: (
            <img
              key={`img-${keyCounter++}`}
              src={match[2]}
              alt={match[1]}
              title={match[3] || undefined}
              className={`${options.classPrefix}image max-w-full h-auto`}
            />
          )
        });
      }
    }
    
    resetPattern(PATTERNS.LINK);
    while ((match = PATTERNS.LINK.exec(text)) !== null) {
      if (!isWithinProcessed(match.index, match.index + match[0].length, processedSegments)) {
        processedSegments.push({
          start: match.index,
          end: match.index + match[0].length,
          element: (
            <a
              key={`link-${keyCounter++}`}
              href={match[2]}
              title={match[3] || undefined}
              className={`${options.classPrefix}link text-blue-600 hover:text-blue-800 underline`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {parseInline(match[1], state, keyCounter + 100)}
            </a>
          )
        });
      }
    }
    
    // Reference links
    resetPattern(PATTERNS.REFERENCE_LINK);
    while ((match = PATTERNS.REFERENCE_LINK.exec(text)) !== null) {
      if (!isWithinProcessed(match.index, match.index + match[0].length, processedSegments)) {
        const refLabel = match[2] || match[1];
        const ref = state.getReference(refLabel);
        if (ref) {
          processedSegments.push({
            start: match.index,
            end: match.index + match[0].length,
            element: (
              <a
                key={`ref-link-${keyCounter++}`}
                href={ref.url}
                title={ref.title || undefined}
                className={`${options.classPrefix}link text-blue-600 hover:text-blue-800 underline`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {parseInline(match[1], state, keyCounter + 100)}
              </a>
            )
          });
        }
      }
    }
    
    // Autolinks
    if (options.autolink) {
      resetPattern(PATTERNS.AUTOLINK);
      while ((match = PATTERNS.AUTOLINK.exec(text)) !== null) {
        if (!isWithinProcessed(match.index, match.index + match[0].length, processedSegments)) {
          processedSegments.push({
            start: match.index,
            end: match.index + match[0].length,
            element: (
              <a
                key={`autolink-${keyCounter++}`}
                href={match[1]}
                className={`${options.classPrefix}link text-blue-600 hover:text-blue-800 underline`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {match[1]}
              </a>
            )
          });
        }
      }
      
      resetPattern(PATTERNS.EMAIL_AUTOLINK);
      while ((match = PATTERNS.EMAIL_AUTOLINK.exec(text)) !== null) {
        if (!isWithinProcessed(match.index, match.index + match[0].length, processedSegments)) {
          processedSegments.push({
            start: match.index,
            end: match.index + match[0].length,
            element: (
              <a
                key={`email-${keyCounter++}`}
                href={`mailto:${match[1]}`}
                className={`${options.classPrefix}link text-blue-600 hover:text-blue-800 underline`}
              >
                {match[1]}
              </a>
            )
          });
        }
      }
    }
    
    // Footnote references
    if (options.footnotes) {
      resetPattern(PATTERNS.FOOTNOTE_REF);
      while ((match = PATTERNS.FOOTNOTE_REF.exec(text)) !== null) {
        if (!isWithinProcessed(match.index, match.index + match[0].length, processedSegments)) {
          const footnote = state.getFootnote(match[1]);
          if (footnote) {
            processedSegments.push({
              start: match.index,
              end: match.index + match[0].length,
              element: (
                <sup key={`footnote-${keyCounter++}`}>
                  <a
                    href={`#fn-${match[1]}`}
                    className={`${options.classPrefix}footnote-ref text-blue-600 hover:text-blue-800`}
                  >
                    [{match[1]}]
                  </a>
                </sup>
              )
            });
          }
        }
      }
    }
  }
  
  // Sort segments by start position
  processedSegments.sort((a, b) => a.start - b.start);
  
  // Build final elements array
  let lastEnd = 0;
  for (const segment of processedSegments) {
    // Add text before this segment
    if (segment.start > lastEnd) {
      const textBefore = text.substring(lastEnd, segment.start);
      const formatted = formatTextEmphasis(textBefore, options, keyCounter);
      if (formatted) {
        elements.push(formatted);
        keyCounter += 100;
      }
    }
    
    // Add the segment element
    elements.push(segment.element);
    lastEnd = segment.end;
  }
  
  // Add any remaining text
  if (lastEnd < text.length) {
    const remainingText = text.substring(lastEnd);
    const formatted = formatTextEmphasis(remainingText, options, keyCounter);
    if (formatted) {
      elements.push(formatted);
    }
  }
  
  return elements.length === 0 ? text : (elements.length === 1 ? elements[0] : elements);
};

/**
 * Check if a range is within already processed segments
 */
const isWithinProcessed = (
  start: number, 
  end: number, 
  processed: Array<{ start: number; end: number }>
): boolean => {
  return processed.some(seg => 
    (start >= seg.start && start < seg.end) || 
    (end > seg.start && end <= seg.end) ||
    (start <= seg.start && end >= seg.end)
  );
};

/**
 * Format text emphasis (bold, italic, strikethrough)
 */
const formatTextEmphasis = (
  text: string, 
  options: MarkdownFormatterOptions, 
  keyBase: number
): ReactNode => {
  if (!text) return null;
  
  let keyCounter = keyBase;
  const elements: ReactNode[] = [];
  const processed: Array<{ start: number; end: number; element: ReactNode }> = [];
  
  // Process bold+italic first (***text***)
  let match;
  resetPattern(PATTERNS.BOLD_ITALIC);
  while ((match = PATTERNS.BOLD_ITALIC.exec(text)) !== null) {
    processed.push({
      start: match.index,
      end: match.index + match[0].length,
      element: (
        <strong key={`bold-italic-${keyCounter++}`} className="font-bold italic">
          {match[1]}
        </strong>
      )
    });
  }
  
  // Process bold (**text**)
  resetPattern(PATTERNS.BOLD);
  while ((match = PATTERNS.BOLD.exec(text)) !== null) {
    if (!isWithinProcessed(match.index, match.index + match[0].length, processed)) {
      processed.push({
        start: match.index,
        end: match.index + match[0].length,
        element: (
          <strong key={`bold-${keyCounter++}`} className="font-semibold">
            {match[1]}
          </strong>
        )
      });
    }
  }
  
  // Process italic (*text*)
  resetPattern(PATTERNS.ITALIC);
  while ((match = PATTERNS.ITALIC.exec(text)) !== null) {
    if (!isWithinProcessed(match.index, match.index + match[0].length, processed)) {
      processed.push({
        start: match.index,
        end: match.index + match[0].length,
        element: (
          <em key={`italic-${keyCounter++}`} className="italic">
            {match[1]}
          </em>
        )
      });
    }
  }
  
  // Process strikethrough (~~text~~)
  if (options.strikethrough) {
    resetPattern(PATTERNS.STRIKETHROUGH);
    while ((match = PATTERNS.STRIKETHROUGH.exec(text)) !== null) {
      if (!isWithinProcessed(match.index, match.index + match[0].length, processed)) {
        processed.push({
          start: match.index,
          end: match.index + match[0].length,
          element: (
            <del key={`strike-${keyCounter++}`} className="line-through text-gray-500">
              {match[1]}
            </del>
          )
        });
      }
    }
  }
  
  // Process special markers (FOLLOW_UP)
  resetPattern(PATTERNS.FOLLOW_UP);
  while ((match = PATTERNS.FOLLOW_UP.exec(text)) !== null) {
    processed.push({
      start: match.index,
      end: match.index + match[0].length,
      element: (
        <span
          key={`followup-${keyCounter++}`}
          className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200 mr-2"
        >
          💡 Follow-up
        </span>
      )
    });
  }
  
  // Process mentions (@username)
  resetPattern(PATTERNS.MENTION);
  while ((match = PATTERNS.MENTION.exec(text)) !== null) {
    if (!isWithinProcessed(match.index, match.index + match[0].length, processed)) {
      processed.push({
        start: match.index,
        end: match.index + match[0].length,
        element: (
          <span
            key={`mention-${keyCounter++}`}
            className="text-blue-600 font-medium"
          >
            @{match[1]}
          </span>
        )
      });
    }
  }
  
  // Process hashtags (#tag)
  resetPattern(PATTERNS.HASHTAG);
  while ((match = PATTERNS.HASHTAG.exec(text)) !== null) {
    if (!isWithinProcessed(match.index, match.index + match[0].length, processed)) {
      processed.push({
        start: match.index,
        end: match.index + match[0].length,
        element: (
          <span
            key={`hashtag-${keyCounter++}`}
            className="text-blue-600"
          >
            #{match[1]}
          </span>
        )
      });
    }
  }
  
  // Process emoji (:emoji:)
  resetPattern(PATTERNS.EMOJI);
  while ((match = PATTERNS.EMOJI.exec(text)) !== null) {
    if (!isWithinProcessed(match.index, match.index + match[0].length, processed)) {
      const emoji = EMOJI_MAP[match[1]];
      if (emoji) {
        processed.push({
          start: match.index,
          end: match.index + match[0].length,
          element: (
            <span key={`emoji-${keyCounter++}`} role="img" aria-label={match[1]}>
              {emoji}
            </span>
          )
        });
      }
    }
  }
  
  // Sort and build final result
  processed.sort((a, b) => a.start - b.start);
  
  let lastEnd = 0;
  for (const segment of processed) {
    if (segment.start > lastEnd) {
      elements.push(text.substring(lastEnd, segment.start));
    }
    elements.push(segment.element);
    lastEnd = segment.end;
  }
  
  if (lastEnd < text.length) {
    elements.push(text.substring(lastEnd));
  }
  
  return elements.length === 0 ? text : (elements.length === 1 ? elements[0] : elements);
};

// ============================================================================
// Copy Markdown as HTML Utility (defined early for use in CopyTableButton)
// ============================================================================

/**
 * Configure marked with GitHub Flavored Markdown options
 */
marked.setOptions({
  gfm: true,
  breaks: false,
  pedantic: false,
  silent: false
});

/**
 * Copies any markdown content as HTML to clipboard
 * Supports all markdown elements: headings, lists, code blocks, tables, links, etc.
 * Falls back to plain markdown if HTML paste is not supported
 * 
 * @param markdown - The markdown text to copy
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 */
export const copyMarkdownAsHTML = async (
  markdown: string
): Promise<boolean> => {
  if (!markdown || !markdown.trim()) {
    return false;
  }

  try {
    // Convert markdown to HTML using marked
    // marked() can return a Promise in async mode, so we handle both cases
    const htmlResult = marked(markdown);
    const html = typeof htmlResult === 'string' ? htmlResult : await htmlResult;

    // Create clipboard item with both HTML and plain text (markdown)
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([markdown], { type: 'text/plain' })
    });

    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (err) {
    console.error('Failed to copy markdown as HTML to clipboard', err);
    
    // Fallback: copy as plain markdown text
    try {
      await navigator.clipboard.writeText(markdown);
      return true;
    } catch (fallbackErr) {
      console.error('Fallback copy also failed', fallbackErr);
      return false;
    }
  }
};

// ============================================================================
// Helper: Reconstruct Markdown from Table Data
// ============================================================================

/**
 * Reconstructs markdown table string from parsed table data
 * Used by CopyTableButton to convert table data back to markdown
 */
const reconstructTableMarkdown = (
  headerRows: string[][],
  bodyRows: string[][],
  alignments: (null | 'left' | 'center' | 'right')[]
): string => {
  const allRows = [...headerRows, ...bodyRows];
  const columnWidths: number[] = [];
  
  // Find max width for each column
  allRows.forEach(row => {
    row.forEach((cell, index) => {
      const cellText = (cell || '').replace(/\r?\n/g, ' ').trim();
      columnWidths[index] = Math.max(columnWidths[index] || 0, cellText.length);
    });
  });
  
  // Helper function to pad cell content
  const padCell = (text: string, width: number): string => {
    return text.padEnd(width, ' ');
  };
  
  let markdown = '';
  
  // Add header rows
  if (headerRows.length > 0) {
    headerRows.forEach(row => {
      const paddedCells = row.map((cell, index) => {
        const cellText = (cell || '').replace(/\r?\n/g, ' ').trim();
        return padCell(cellText, columnWidths[index]);
      });
      markdown += '| ' + paddedCells.join(' | ') + ' |\n';
    });
    
    // Add separator row with proper width and alignment
    const separatorCells = headerRows[0].map((_, index) => {
      const align = alignments[index];
      const width = columnWidths[index];
      
      if (align === 'center') {
        const dashes = '-'.repeat(Math.max(width, 3));
        return ':' + dashes.slice(0, -1) + ':';
      } else if (align === 'right') {
        const dashes = '-'.repeat(Math.max(width, 3));
        return dashes.slice(0, -1) + ':';
      } else {
        return '-'.repeat(Math.max(width, 3));
      }
    });
    markdown += '| ' + separatorCells.join(' | ') + ' |\n';
  }
  
  // Add body rows
  bodyRows.forEach(row => {
    const paddedCells = row.map((cell, index) => {
      const cellText = (cell || '').replace(/\r?\n/g, ' ').trim();
      return padCell(cellText, columnWidths[index]);
    });
    markdown += '| ' + paddedCells.join(' | ') + ' |\n';
  });
  
  return markdown.trim();
};

// ============================================================================
// Block Parsing Functions
// ============================================================================

/**
 * Parse a code block
 */
const parseCodeBlock = (state: ParserState): ReactElement | null => {
  const line = state.getCurrentLine();
  if (!line || !line.match(PATTERNS.CODE_BLOCK)) return null;
  
  const match = line.match(PATTERNS.CODE_BLOCK);
  const language = match?.[1] || '';
  // const meta = match?.[2] || ''; // Meta information for code blocks (not used yet)
  const lines: string[] = [];
  
  state.advance();
  
  while (!state.isComplete()) {
    const currentLine = state.getCurrentLine();
    if (currentLine?.match(PATTERNS.CODE_BLOCK)) {
      state.advance();
      break;
    }
    lines.push(currentLine || '');
    state.advance();
  }
  
  const content = lines.join('\n');
  // const options = state.getOptions(); // Could be used for syntax highlighting
  
  // Component to handle copy button with state
  const CopyCodeButton = () => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(content);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code to clipboard', err);
      }
    };

    return (
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 h-auto p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-transparent"
        onClick={handleCopy}
        title={isCopied ? 'Copied!' : 'Copy code'}
        aria-label="Copy code"
      >
        <Icon
          icon={isCopied ? Check : Copy}
          size={16}
          strokeWidth={2}
        />
      </Button>
    );
  };
  
  return (
    <div className="relative mb-3 group">
      {language && (
        <div className="absolute top-0 right-0 px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded-bl">
          {language}
        </div>
      )}
      <CopyCodeButton />
      <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm font-mono">
        <code className={language ? `language-${language}` : ''}>
          {content}
        </code>
      </pre>
    </div>
  );
};

/**
 * Check if the current position starts a valid table
 */
const isValidTableStart = (state: ParserState): boolean => {
  const currentPos = state.getPosition();
  const lines = state.getAllLines();
  
  // Look for at least one valid table row with multiple cells
  let validRowsFound = 0;
  let pos = currentPos;
  let hasSeparator = false;
  
  while (pos < lines.length && pos < currentPos + 10) { // Limit lookahead to 10 lines
    const line = lines[pos];
    if (!line || !line.match(PATTERNS.TABLE_ROW)) break;
    
    if (line.match(PATTERNS.TABLE_SEPARATOR)) {
      hasSeparator = true;
    } else {
      const cells = line.split('|').slice(1, -1).map(cell => cell.trim());
      // A valid table row should have at least 2 cells, with at least one non-empty
      if (cells.length >= 2 && cells.some(cell => cell.length > 0)) {
        validRowsFound++;
      } else if (cells.length === 1 && cells[0].length > 0) {
        // Single cell might be valid in some cases, but be more cautious
        // Only count it if it contains typical table content (not just text)
        const content = cells[0];
        if (content.includes('|') || content.match(/^\s*[\w\s]+\s*$/)) {
          // This might be malformed - be cautious
          return false;
        }
      }
    }
    
    pos++;
    
    // If we found valid rows or a separator, it's likely a table
    if (validRowsFound >= 1 || hasSeparator) return true;
  }
  
  return false;
};

/**
 * Parse a table
 */
const parseTable = (state: ParserState): ReactElement | null => {
  const line = state.getCurrentLine();
  if (!line || !line.match(PATTERNS.TABLE_ROW)) return null;
  
  // Validate this is actually a table before starting
  if (!isValidTableStart(state)) {
    return null;
  }
  
  const rows: string[][] = [];
  let alignments: (null | 'left' | 'center' | 'right')[] = [];
  let headerRowIndex = -1;
  
  // Collect all table rows
  while (!state.isComplete()) {
    const currentLine = state.getCurrentLine();
    if (!currentLine || !currentLine.match(PATTERNS.TABLE_ROW)) break;
    
    // Check if this is a separator row
    if (currentLine.match(PATTERNS.TABLE_SEPARATOR)) {
      headerRowIndex = rows.length - 1;
      alignments = parseTableAlignment(currentLine);
    } else {
      const cells = currentLine
        .split('|')
        .slice(1, -1)
        .map(cell => cell.trim());
      
      // Only add non-empty cell arrays
      if (cells.length > 0) {
        rows.push(cells);
      }
    }
    state.advance();
  }
  
  if (rows.length === 0) return null;
  
  // Build table element
  const hasHeader = headerRowIndex >= 0;
  const headerRows = hasHeader ? rows.slice(0, headerRowIndex + 1) : [];
  const bodyRows = hasHeader ? rows.slice(headerRowIndex + 1) : rows;
  
  // Prepare TSV for clipboard (tab-separated values) so pasting into sheets preserves columns
  const tsv = rows
    .map(r => r.map(c => (c || '').replace(/\r?\n/g, ' ').replace(/\t/g, ' ').trim()).join('\t'))
    .join('\n');

  // Component to handle copy button with state
  const CopyTableButton = () => {
    const [isCopied, setIsCopied] = useState(false);

      const handleCopy = async () => {
      // Reconstruct markdown from table data and use copyMarkdownAsHTML
      const tableMarkdown = reconstructTableMarkdown(headerRows, bodyRows, alignments);
      const success = await copyMarkdownAsHTML(tableMarkdown);
      if (success) {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    };

    return (
      <Button
        variant="ghost"
        size="sm"
        className={`absolute -top-1 h-auto  opacity-0 group-hover:opacity-100 transition-opacity hover:bg-transparent ${
          state.getOptions().isRtl ? '-left-1' : '-right-1'
        }`}
        onClick={handleCopy}
        title={isCopied ? 'Copied!' : 'Copy table (paste into spreadsheet)'}
        aria-label="Copy table"
      >
        <Icon
          icon={isCopied ? Check : Copy}
          size={16}
          strokeWidth={2}
        />
      </Button>
    );
  };

  return (
    <div className={`relative overflow-x-auto mb-3 max-w-full scrollbar-chat group ${state.getOptions().isRtl ? 'rtl' : ''}`}>
      <table className={`relative min-w-full max-w-full border-collapse border text-sm border-gray-300 ${state.getOptions().isRtl ? 'dir-rtl' : ''}`}>
        {headerRows.length > 0 && (
          <thead>
            {headerRows.map((row, rowIndex) => {
              // Normalize row to have consistent cell count
              const maxCells = Math.max(...rows.map(r => r.length));
              const normalizedRow = [...row];
              while (normalizedRow.length < maxCells) {
                normalizedRow.push(''); // Add empty cells if needed
              }
              
              return (
                <tr key={`thead-row-${rowIndex}`}>
                  {normalizedRow.map((cell, cellIndex) => {
                        // For RTL, reverse the alignment logic
                        let textAlign = alignments[cellIndex] || 'left';
                        if (state.getOptions().isRtl) {
                          if (textAlign === 'left') textAlign = 'right';
                          else if (textAlign === 'right') textAlign = 'left';
                        }
                    
                        const isLastCell = cellIndex === normalizedRow.length - 1 && rowIndex === 0;
                        if (isLastCell) {
                          return (
                            <th
                              key={`th-${cellIndex}`}
                              className={`border border-gray-300 px-3 py-2 bg-gray-50 font-semibold text-${textAlign}`}
                              dir={state.getOptions().isRtl ? 'rtl' : 'ltr'}
                            >
                                <div>{cell ? parseTableCell(cell, state, cellIndex * 100) : ''}</div>
                                <CopyTableButton />
                            </th>
                          );
                        }

                        return (
                          <th
                            key={`th-${cellIndex}`}
                            className={`border border-gray-300 px-3 py-2 bg-gray-50 font-semibold text-${textAlign}`}
                            dir={state.getOptions().isRtl ? 'rtl' : 'ltr'}
                          >
                            {cell ? parseTableCell(cell, state, cellIndex * 100) : ''}
                          </th>
                        );
                      })}
                </tr>
              );
            })}
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, rowIndex) => {
            // Normalize row to have consistent cell count
            const maxCells = Math.max(...rows.map(r => r.length));
            const normalizedRow = [...row];
            while (normalizedRow.length < maxCells) {
              normalizedRow.push(''); // Add empty cells if needed
            }
            
            return (
              <tr key={`tbody-row-${rowIndex}`}>
                {normalizedRow.map((cell, cellIndex) => {
                  // For RTL, reverse the alignment logic
                  let textAlign = alignments[cellIndex] || 'left';
                  if (state.getOptions().isRtl) {
                    if (textAlign === 'left') textAlign = 'right';
                    else if (textAlign === 'right') textAlign = 'left';
                  }
                  
                  const isLastCellInFirstBodyRow = headerRows.length === 0 && rowIndex === 0 && cellIndex === normalizedRow.length - 1;
                  if (isLastCellInFirstBodyRow) {
                    return (
                      <td
                        key={`td-${cellIndex}`}
                        className={`border border-gray-300 px-3 py-2 text-${textAlign}`}
                        dir={state.getOptions().isRtl ? 'rtl' : 'ltr'}
                      >
                        <div className="relative">
                          <div>{cell ? parseTableCell(cell, state, (rowIndex * 100) + cellIndex) : ''}</div>
                          <CopyTableButton />
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={`td-${cellIndex}`}
                      className={`border border-gray-300 px-3 py-2 text-${textAlign}`}
                      dir={state.getOptions().isRtl ? 'rtl' : 'ltr'}
                    >
                      {cell ? parseTableCell(cell, state, (rowIndex * 100) + cellIndex) : ''}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Parse a list (ordered, unordered, or task)
 */
const parseList = (state: ParserState): ReactElement | null => {
  const startLine = state.getCurrentLine();
  if (!startLine) return null;
  
  // Check what type of list this is
  const taskMatch = startLine.match(PATTERNS.TASK_LIST);
  const listMatch = startLine.match(PATTERNS.LIST_ITEM);
  
  if (!taskMatch && !listMatch) return null;
  
  const match = taskMatch || listMatch;
  if (!match) return null;
  
  const [, indent, marker] = match;
  const baseIndent = indent.length;
  const isOrdered = /^\d+[.):]/.test(marker);
  const isTask = !!taskMatch;
  
  const listType = isTask ? 'task' : (isOrdered ? 'ol' : 'ul');
  const listItems: ReactElement[] = [];
  
  // Track ordered list numbering
  let orderStart = 1;
  if (isOrdered) {
    const numMatch = marker.match(/^(\d+)/);
    if (numMatch) {
      orderStart = parseInt(numMatch[1], 10);
    }
  }
  
  // Create list context
  const listContext: ListContext = {
    type: listType as 'ul' | 'ol' | 'task',
    depth: state.getListDepth(),
    start: orderStart,
    marker
  };
  
  state.enterList(listContext);
  
  // Parse list items
  while (!state.isComplete()) {
    const currentLine = state.getCurrentLine();
    if (!currentLine) break;
    
    // Check if this line is a list item
    const currentTaskMatch = currentLine.match(PATTERNS.TASK_LIST);
    const currentListMatch = currentLine.match(PATTERNS.LIST_ITEM);
    
    if (!currentTaskMatch && !currentListMatch) {
      // Check if it's a continuation (indented content)
      if (currentLine.match(/^\s+/) && currentLine.trim()) {
        // Add to previous list item as continuation
        if (listItems.length > 0) {
          const lastItem = listItems[listItems.length - 1] as ReactElement;
          const existingChildren = (lastItem.props as any).children;
          // Ensure children is always an array for spreading
          const childrenArray = Array.isArray(existingChildren) 
            ? existingChildren 
            : existingChildren ? [existingChildren] : [];
          const children = [...childrenArray];
          
          const trimmedLine = currentLine.trim();
          const cleanedLine = cleanListContent(trimmedLine);
          
          // Always add a line break for continuation lines to ensure proper formatting
          children.push(<br key={`br-${state.getPosition()}`} />);
          children.push(parseInline(cleanedLine, state, state.getPosition() * 100));
          
          listItems[listItems.length - 1] = cloneElement(lastItem, {
            children
          } as any);
        }
        state.advance();
        continue;
      }
      // Check for empty lines that might separate list items but not end the list
      if (currentLine.trim() === '') {
        // Look ahead to see if the next non-empty line is a list item of the same type
        let lookAheadIndex = state.getPosition() + 1;
        const lines = state.getAllLines();
        
        while (lookAheadIndex < lines.length && lines[lookAheadIndex].trim() === '') {
          lookAheadIndex++;
        }
        
        if (lookAheadIndex < lines.length) {
          const nextNonEmptyLine = lines[lookAheadIndex];
          const nextMatch = nextNonEmptyLine.match(PATTERNS.LIST_ITEM);
          
          if (nextMatch) {
            const [, nextIndent, nextMarker] = nextMatch;
            const nextIsOrdered = /^\d+[.):]/.test(nextMarker);
            
            // If it's the same type of list and indent level, continue the list
            if (nextIsOrdered === isOrdered && nextIndent.length === baseIndent) {
              // Skip the empty line and continue
              state.advance();
              continue;
            }
          }
        }
      }
      // Not a list item or continuation
      break;
    }
    
    const currentMatch = currentTaskMatch || currentListMatch;
    if (!currentMatch) break;
    
    const [, currentIndent] = currentMatch; // currentMarker not used currently
    
    // Check if this is a different list type or depth
    if (currentIndent.length < baseIndent) {
      // Less indented - end this list
      break;
    }
    
    if (currentIndent.length > baseIndent) {
      // More indented - nested list
      const nestedList = parseList(state);
      if (nestedList && listItems.length > 0) {
        const lastItem = listItems[listItems.length - 1] as ReactElement;
        const existingChildren = (lastItem.props as any).children;
        // Ensure children is always an array for spreading
        const childrenArray = Array.isArray(existingChildren) 
          ? existingChildren 
          : existingChildren ? [existingChildren] : [];
        const children = [...childrenArray, nestedList];
        listItems[listItems.length - 1] = cloneElement(lastItem, { children } as any);
      }
      continue;
    }
    
    // Same level - add to current list
    if (currentTaskMatch) {
      const [, , , checked, content] = currentTaskMatch;
      const cleanedContent = cleanListContent(content);
      listItems.push(
        <li key={`task-${state.getPosition()}`} className="list-none mb-1">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={checked.toLowerCase() === 'x'}
              disabled
              className="mt-1 mr-2"
            />
            <span>{parseInline(cleanedContent, state, state.getPosition() * 100)}</span>
          </label>
        </li>
      );
    } else if (currentListMatch) {
      const [, , , content] = currentListMatch;
      const cleanedContent = cleanListContent(content);
      
      // Check if content already has its own numbering/lettering
      const hasNumbering = hasOwnNumbering(content);
      const itemClass = hasNumbering ? "list-none mb-1" : "mb-1";
      
      listItems.push(
        <li key={`item-${state.getPosition()}`} className={itemClass}>
          {parseInline(cleanedContent, state, state.getPosition() * 100)}
        </li>
      );
    }
    
    state.advance();
  }
  
  state.exitList();
  
  if (listItems.length === 0) return null;
  
  // Check if all items have their own numbering to determine list styling
  const allItemsHaveNumbering = listItems.every(item => {
    const className = (item.props as any).className || '';
    return className.includes('list-none');
  });
  
  // Create the list element
  const ListComponent = listType === 'ol' ? 'ol' : 'ul';
  let listClass: string;
  
  if (listType === 'task') {
    listClass = 'ml-2 mb-3';
  } else if (allItemsHaveNumbering) {
    // If all items have their own numbering, don't add list styling
    listClass = 'ml-6 mb-3';
  } else {
    // Use default list styling
    listClass = listType === 'ol' 
      ? 'list-decimal ml-6 mb-3'
      : 'list-disc ml-6 mb-3';
  }
  
  const listProps: any = {
    className: listClass,
    children: listItems
  };
  
  if (listType === 'ol' && orderStart !== 1) {
    listProps.start = orderStart;
  }
  
  return React.createElement(ListComponent, listProps);
};

/**
 * Parse a blockquote
 */
const parseBlockquote = (state: ParserState): ReactElement | null => {
  const lines: string[] = [];
  
  while (!state.isComplete()) {
    const line = state.getCurrentLine();
    if (!line || !line.match(PATTERNS.BLOCKQUOTE)) break;
    
    const match = line.match(PATTERNS.BLOCKQUOTE);
    if (match) {
      lines.push(match[1]);
    }
    state.advance();
  }
  
  if (lines.length === 0) return null;
  
  // Parse the blockquote content recursively
  const content = lines.join('\n');
  const innerState = new ParserState(content, state.getOptions());
  const elements = parseBlocks(innerState);
  
  return (
    <blockquote className="border-l-4 border-gray-300 pl-4 my-4 italic text-gray-700">
      {elements}
    </blockquote>
  );
};

/**
 * Parse a heading
 */
const parseHeading = (state: ParserState): ReactElement | null => {
  const line = state.getCurrentLine();
  if (!line) return null;
  
  // ATX-style headings (# Heading)
  let match = line.match(PATTERNS.HEADING_WITH_ID);
  if (match) {
    const [, hashes, content, id] = match;
    const level = Math.min(hashes.length, 6) as 1 | 2 | 3 | 4 | 5 | 6;
    state.advance();
    return createHeading(level, content, id, state);
  }
  
  match = line.match(PATTERNS.HEADING);
  if (match) {
    const [, hashes, content] = match;
    const level = Math.min(hashes.length, 6) as 1 | 2 | 3 | 4 | 5 | 6;
    state.advance();
    return createHeading(level, content, null, state);
  }
  
  // Setext-style headings (underlined)
  const nextLine = state.peekLine();
  if (nextLine) {
    if (nextLine.match(/^=+$/)) {
      state.advance();
      state.advance();
      return createHeading(1, line, null, state);
    }
    if (nextLine.match(/^-+$/)) {
      state.advance();
      state.advance();
      return createHeading(2, line, null, state);
    }
  }
  
  return null;
};

/**
 * Create a heading element
 */
const createHeading = (
  level: 1 | 2 | 3 | 4 | 5 | 6,
  content: string,
  customId: string | null,
  state: ParserState
): ReactElement => {
  const options = state.getOptions();
  const id = customId || (options.headingAnchors ? generateSlug(content) : undefined);
  
  const HeadingTag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const classNames = [
    'font-semibold',
    level === 1 ? 'text-xl mb-3 mb-3' :
    level === 2 ? 'text-lg mb-2 mb-3' :
    level === 3 ? 'text-base mb-2 mb-3' :
    level === 4 ? 'text-sm mb-1 mb-2' :
    level === 5 ? 'text-sm mb-1 mb-2' :
    'text-sm mb-1 mb-1'
  ].join(' ');
  
  return React.createElement(
    HeadingTag,
    { id, className: classNames },
    [
      parseInline(content, state, 0),
      options.headingAnchors && id && (
        <a
          key="anchor"
          href={`#${id}`}
          className="ml-2 text-gray-400 hover:text-gray-600 opacity-0 hover:opacity-100 transition-opacity"
          aria-label="Anchor link"
        >
          #
        </a>
      )
    ].filter(Boolean)
  );
};

/**
 * Parse a paragraph
 */
const parseParagraph = (state: ParserState): ReactElement | null => {
  const lines: string[] = [];
  
  while (!state.isComplete()) {
    const line = state.getCurrentLine();
    if (!line || line.trim() === '') break;
    
    // Check if this line starts a different block
    if (
      line.match(PATTERNS.HEADING) ||
      line.match(PATTERNS.HEADING_WITH_ID) ||
      line.match(PATTERNS.HORIZONTAL_RULE) ||
      line.match(PATTERNS.BLOCKQUOTE) ||
      line.match(PATTERNS.CODE_BLOCK) ||
      line.match(PATTERNS.LIST_ITEM) ||
      line.match(PATTERNS.TASK_LIST) ||
      line.match(PATTERNS.TABLE_ROW) ||
      line.match(PATTERNS.HTML_BLOCK)
    ) {
      break;
    }
    
    // Also check for setext-style headings (underlined)
    const nextLine = state.peekLine();
    if (nextLine && (nextLine.match(/^=+$/) || nextLine.match(/^-+$/))) {
      break;
    }
    
    lines.push(line);
    state.advance();
  }
  
  if (lines.length === 0) return null;
  
  const options = state.getOptions();
  const content = options.breaks 
    ? lines.join('\n').replace(/\n/g, '<br />') 
    : lines.join(' ');
  
  return (
    <p className="mb-3 leading-relaxed">
      {parseInline(content, state, 0)}
    </p>
  );
};

/**
 * Parse all blocks in the document
 */
const parseBlocks = (state: ParserState): ReactElement[] => {
  const blocks: ReactElement[] = [];
  let blockIndex = 0;
  let previousBlockType: string | null = null;
  
  while (!state.isComplete()) {
    const line = state.getCurrentLine();
    
    // Skip empty lines
    if (!line || line.trim() === '') {
      state.advance();
      continue;
    }
    
    // Try to parse different block types
    let block: ReactElement | null = null;
    let currentBlockType: string | null = null;
    
    // Check for horizontal rule
    if (line.match(PATTERNS.HORIZONTAL_RULE)) {
      block = <hr key={`hr-${blockIndex++}`} className="border-t border-gray-300 my-4" />;
      currentBlockType = 'hr';
      state.advance();
    }
    // Code block
    else if (line.match(PATTERNS.CODE_BLOCK)) {
      block = parseCodeBlock(state);
      currentBlockType = 'code';
    }
    // Table
    else if (line.match(PATTERNS.TABLE_ROW) && state.getOptions().tables) {
      block = parseTable(state);
      currentBlockType = 'table';
      // If table parsing failed, treat as regular paragraph
      if (!block) {
        block = parseParagraph(state);
        currentBlockType = 'paragraph';
      }
    }
    // Heading
    else if (line.match(PATTERNS.HEADING) || line.match(PATTERNS.HEADING_WITH_ID) || state.peekLine()?.match(/^[=-]+$/)) {
      block = parseHeading(state);
      currentBlockType = 'heading';
    }
    // Blockquote
    else if (line.match(PATTERNS.BLOCKQUOTE)) {
      block = parseBlockquote(state);
      currentBlockType = 'blockquote';
    }
    // List
    else if (line.match(PATTERNS.LIST_ITEM) || line.match(PATTERNS.TASK_LIST)) {
      block = parseList(state);
      
      // Determine list type for tracking
      const taskMatch = line.match(PATTERNS.TASK_LIST);
      const listMatch = line.match(PATTERNS.LIST_ITEM);
      const match = taskMatch || listMatch;
      
      if (match) {
        const [, , marker] = match;
        const isOrdered = /^\d+[.):]/.test(marker);
        const isTask = !!taskMatch;
        currentBlockType = isTask ? 'task' : (isOrdered ? 'ol' : 'ul');
        
        // If current block is ul and previous was ol, add ml-12 class
        if (currentBlockType === 'ul' && previousBlockType === 'ol' && block) {
          const currentClassName = (block.props as any).className || '';
          const newClassName = currentClassName.includes('ml-12') 
            ? currentClassName 
            : `${currentClassName} ml-12`.trim();
          
          block = React.cloneElement(block, {
            className: newClassName
          } as any);
        }
      }
    }
    // Paragraph (default)
    else {
      block = parseParagraph(state);
      currentBlockType = 'paragraph';
    }
    
    if (block) {
      blocks.push(React.cloneElement(block, { key: `block-${blockIndex++}` }));
      previousBlockType = currentBlockType;
    } else {
      // If no block was parsed, advance to prevent infinite loop
      state.advance();
    }
  }
  
  return blocks;
};

// ============================================================================
// Main Formatting Function
// ============================================================================

/**
 * Format markdown text into React elements
 */
export const formatMessage = (
  text: string,
  options: MarkdownFormatterOptions = {}
): ReactNode => {
  // Clean the input
  const cleanedText = text;

  // Create parser state
  const state = new ParserState(cleanedText, options);
  
  // Parse blocks
  const blocks = parseBlocks(state);
  
  // Add footnotes section if there are any
  const footnotes = Array.from(state.getUsedFootnotes()).map(label => {
    const content = state.getFootnote(label);
    if (!content) return null;
    
    return (
      <div key={`fn-${label}`} id={`fn-${label}`} className="text-sm mb-1">
        <span className="font-semibold">[{label}]:</span> {content}
      </div>
    );
  }).filter(Boolean);
  
  if (footnotes.length > 0) {
    blocks.push(
      <div key="footnotes" className="border-t border-gray-300 mt-8 pt-4">
        <h4 className="font-semibold text-lg mb-1">Footnotes</h4>
        {footnotes}
      </div>
    );
  }
  
  return blocks;
};

// ============================================================================
// React Component Wrapper
// ============================================================================

export interface MarkdownFormatterProps {
  text: string;
  options?: MarkdownFormatterOptions;
  className?: string;
}

/**
 * React component for rendering markdown
 */
export const MarkdownFormatter = memo<MarkdownFormatterProps>(({
  text,
  options = {},
  className = ''
}) => {
  const formatted = useMemo(
    () => formatMessage(text, options),
    [text, options]
  );
  
  return (
    <div className={`markdown-content ${className}`}>
      {formatted}
    </div>
  );
});

MarkdownFormatter.displayName = 'MarkdownFormatter';

// ============================================================================
// Streaming-Safe Formatting Utilities
// ============================================================================

/**
 * Checks if a markdown string appears to be incomplete/cut off
 */
export const isIncompleteMarkdown = (text: string): boolean => {
  const trimmed = text.trim();
  
  // Check for incomplete code blocks
  const codeBlockMatches = trimmed.match(/```/g);
  if (codeBlockMatches && codeBlockMatches.length % 2 !== 0) {
    return true;
  }
  
  // Check for incomplete inline code
  const inlineCodeMatches = trimmed.match(/`/g);
  if (inlineCodeMatches && inlineCodeMatches.length % 2 !== 0) {
    return true;
  }
  
  // Check for incomplete bold/italic
  const boldMatches = trimmed.match(/\*\*/g);
  if (boldMatches && boldMatches.length % 2 !== 0) {
    return true;
  }
  
  // Check for incomplete italic - but exclude asterisks that are part of bold markers (**) or list bullets
  // First, remove all ** pairs (bold markers) to check only standalone * (italic markers)
  const textWithoutBold = trimmed.replace(/\*\*/g, '');
  // Remove list bullets (* at start of line followed by space) - these are not italic markers
  const textWithoutListBullets = textWithoutBold.replace(/^\s*\*\s+/gm, '');
  const italicMatches = textWithoutListBullets.match(/\*/g);
  if (italicMatches && italicMatches.length % 2 !== 0) {
    return true;
  }
  
  // Check for incomplete links
  if (trimmed.match(/\[[^\]]*$/)) {
    return true;
  }
  
  // Check for incomplete images
  if (trimmed.match(/!\[[^\]]*$/)) {
    return true;
  }
  
  // Check for incomplete lists (ends mid-item)
  const lines = trimmed.split('\n');
  const lastLine = lines[lines.length - 1];
  if (lastLine && lastLine.match(/^(\s*)([-*+]|\d+[.):])\s*$/)) {
    return true;
  }
  
  return false;
};

/**
 * Finds the last safe point to cut the text for streaming display
 */
export const findSafeStreamingCutPoint = (text: string): number => {
  const lines = text.split('\n');
  let safeIndex = 0;
  let currentIndex = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineWithNewline = i === lines.length - 1 ? line : line + '\n';
    
    // Check if this line would create incomplete markdown
    const testText = text.substring(0, currentIndex + lineWithNewline.length);
    const isIncomplete = isIncompleteMarkdown(testText);
    
    if (!isIncomplete) {
      safeIndex = currentIndex + lineWithNewline.length;
    }
    
    currentIndex += lineWithNewline.length;
  }
  
  // If no safe point found, return at least the first complete sentence
  if (safeIndex === 0) {
    const sentenceMatch = text.match(/^[^.!?]*[.!?]\s*/);
    if (sentenceMatch) {
      safeIndex = sentenceMatch[0].length;
    }
  }
  
  return safeIndex;
};

/**
 * Detect if text should use RTL direction based on Arabic character percentage
 */
export const hasArabicCharacters = (text: string): boolean => {
  // Count Arabic vs non-Arabic characters
  const arabicMatches = text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g);
  
  // Remove whitespace and punctuation for more accurate counting
  const meaningfulText = text.replace(/[\s\n\r\t.,!?;:"'()[\]{}\-_=+*/\\|<>@#$%^&`~]/g, '');
  const totalChars = meaningfulText.length;
  
  if (totalChars === 0) return false;
  
  const arabicCount = arabicMatches ? arabicMatches.length : 0;
  
  // Use RTL if Arabic characters make up more than 25% of the meaningful content
  return (arabicCount / totalChars) > 0.25;
};
/**
 * Format message safely for streaming - only shows complete, safe-to-format content
 */
export const formatMessageStreaming = (
  text: string,
  options: MarkdownFormatterOptions = {},
  detectedLanguage?: 'rtl' | 'ltr'
): { safeContent: ReactNode; hasMore: boolean; isRtl: boolean } => {
  if (!text.trim()) {
    return { safeContent: null, hasMore: false, isRtl: false };
  }
  
  // Use detected language if provided, otherwise detect from content
  const isRtl = detectedLanguage 
    ? detectedLanguage === 'rtl'
    : hasArabicCharacters(text);
  
  // Check if the full text is complete markdown (not incomplete)
  const isIncomplete = isIncompleteMarkdown(text);
  const isComplete = !isIncomplete;
  
  // If the message is complete, use the full text
  // Otherwise, find a safe cut point for streaming
  let textToFormat: string;
  let hasMore: boolean;
  
  if (isComplete) {
    // Message is complete - use full text
    textToFormat = text;
    hasMore = false;
  } else {
    // Message is incomplete - find safe cut point for streaming
    const safeCutPoint = findSafeStreamingCutPoint(text);
    textToFormat = text.substring(0, safeCutPoint);
    hasMore = safeCutPoint < text.length;
  }
  
  if (!textToFormat.trim()) {
    // Return raw text if no safe formatting point found
    // return { 
    //   safeContent: <span>{text}</span>, 
    //   hasMore: false 
    // };
  }
  
  // Pass RTL information to formatMessage
  const optionsWithRtl = { ...options, isRtl };
  
  const formattedContent = formatMessage(textToFormat, optionsWithRtl);
  
  return {
    safeContent: formattedContent,
    hasMore,
    isRtl
  };
};


// ============================================================================
// Export utilities for external use
// ============================================================================

export const MarkdownUtils = {
  cleanMessage,
  cleanUserMessageWithAttachments,
  escapeHtml,
  generateSlug,
  parseInline: (text: string, options?: MarkdownFormatterOptions) => {
    const state = new ParserState('', options);
    return parseInline(text, state, 0);
  },
  isIncompleteMarkdown,
  findSafeStreamingCutPoint,
  formatMessageStreaming: (text: string, options?: MarkdownFormatterOptions, detectedLanguage?: 'rtl' | 'ltr') => 
    formatMessageStreaming(text, options, detectedLanguage),
  hasArabicCharacters,
  copyMarkdownAsHTML
};

export default MarkdownFormatter;